/**
 *  @swagger
 *  definitions:
 *    CrewmemberAccessTokenParams:
 *      type: object
 *      properties:
 *        CrewmemberId:
 *          description: ID of the associated Crewmember
 *          type: string
 *        Name:
 *          description: Name of the CrewmemberAccessToken
 *          type: string
 *        Exp:
 *          description: Expire date of the CrewmemberAccessToken
 *          type: string
 *          format: date-time
 *          nullable: true
 *        Status:
 *          description: Status of the CrewmemberAccessToken
 *          $ref: '#/components/enums/CrewmemberAccessTokenStatus'
 *        Agent:
 *          description: Agent of the CrewmemberAccessToken
 *          type: object
 *          properties:
 *            platform:
 *              type: string
 *            os:
 *              type: string
 *            browser:
 *              type: string
 *            ip:
 *              type: string
 *            country:
 *              type: string
 *            city:
 *              type: string
 *        AuthType:
 *          description: Auth Type of the CrewmemberAccessToken
 *          $ref: '#/components/enums/CrewmemberAccessTokenAuthType'
 *
 *  @swagger
 *  definitions:
 *    CrewmemberAccessTokenCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/CrewmemberAccessTokenParams'
 *        - type: object
 *          required:
 *            - CrewmemberId
 *            - Name
 *            - Exp
 *            - Status
 *            - Agent
 *            - AuthType
 *
 *  @swagger
 *  definitions:
 *    CrewmemberAccessTokenQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the CrewmemberAccessToken
 *              type: string
 *        - $ref: '#/definitions/CrewmemberAccessTokenParams'
 *
 *  @swagger
 *  definitions:
 *    CrewmemberAccessTokenUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/CrewmemberAccessTokenQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    CrewmemberAccessToken:
 *      allOf:
 *        - $ref: '#/definitions/CrewmemberAccessTokenCreateParams'
 *        - $ref: '#/definitions/CrewmemberAccessTokenUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      CrewmemberAccessToken:
 *        allOf:
 *          - $ref: '#/definitions/CrewmemberAccessToken'
 *          - type: object
 *            properties:
 *              Crewmember:
 *                description: Crewmember of the CrewmemberAccessToken
 *                $ref: '#/components/schemas/Crewmember'
 */

// Enums
import {
  CrewmemberAccessTokenAuthType,
  CrewmemberAccessTokenStatus,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Agent,
  $Field,
} from '../types';

export type $RelationIds = {
  CrewmemberId: string;
};

export type $Params = {
  Agent: $Agent;
  AuthType: CrewmemberAccessTokenAuthType;
  Exp: Date;
  Name: string;
  Status: CrewmemberAccessTokenStatus;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Crewmember?: $Model<'Crewmember'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Agent: {
    allowNull: false,
    type: Sequelize.JSON,
    validate: {
      notEmpty: true,
    },
  },
  AuthType: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(CrewmemberAccessTokenAuthType)),
    validate: {
      notEmpty: true,
    },
  },
  Exp: {
    allowNull: false,
    type: Sequelize.DATE,
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Status: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(CrewmemberAccessTokenStatus)),
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'CrewmemberAccessToken'> => {
  const CrewmemberAccessToken = stack.define(
    'CrewmemberAccessToken',
    fields(Sequelize),
    {
      tableName: 'CrewmemberAccessTokens',
    },
  );

  CrewmemberAccessToken.associate = (models: $ModelClasses) => {
    models.CrewmemberAccessToken.belongsTo(
      models.Crewmember,
      {
        as: 'Crewmember',
        foreignKey: 'CrewmemberId',
      },
    );
  };

  CrewmemberAccessToken.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return CrewmemberAccessToken;
});
